import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  ButtonBase,
  debounce,
  IconButton,
  Slider,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DropdownSelector } from "./DropdownSelector";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import CircularProgress from "@mui/material/CircularProgress";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RefreshIcon from "@mui/icons-material/Refresh";
import SignalRService from "../../one-query/services/SignalRService";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import {
  useGetTextSuitSamples,
  useTextSuitesAIDetector,
  useTextSuitesHumanizer,
  useTextSuitesParaphrase,
  useTextSuitesPromptEngineer,
  useTextSuitesSummarize,
  useTextSuitesTitleGenerator,
  useTextSuitesTranslate,
} from "../../../store/Apis/SuitesApps/TextSuitesApi";
import {
  CreateWorkflow,
  CreateWorkflowPreview,
  getSummarizeOutputKeyIndexByValue,
  MessageType,
  SummarizeOutputValues,
  textSuiteAppInitStates,
  TextSuiteApps,
  TextSuites,
  TextSuiteSample,
  TextSuiteType,
} from "../../../Helper";
import ChatResponseFormat from "../../main-page/components/get-conversation/ChatResponseFormat";
import { ClearIcon } from "../../../Custom-imeges/SuitesIcons/TextSuitesIcons/ClearIcon";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setFileError,
  setShowSignPopup,
} from "../../../store/one-query/chatsSlice";
import { ActiveItemInterface } from "../Views/TextSuiteSidebar";
import { ImageToPromptApp } from "../../ImageSuite/Components/Apps/ImageToPromptApp";
import { TextExtractionApp } from "../../ImageSuite/Components/Apps/TextExtractionApp";
import { setMessageErrorAppSuite } from "../../../store/account/userSlice";
import { userGetImageSuiteSamples } from "../../../store/Apis/SuitesApps/ImageSuitesApi";
import {
  TextSuiteAppInputImage,
  TextSuiteAppInputText,
} from "../../../Types/TextSuite-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetTextSuiteHistoryById } from "../../../store/Apis/SuitesApps/SuiteHistroyApi";
import { useGetUserCredits } from "../../../store/Apis/StripeApi";

export const tooltipStyles = {
  tooltip: {
    sx: {
      bgcolor: "#E5E2E1",
      color: "#0B1216",
      fontSize: "11px",
      fontWeight: 500,
      padding: "8px 12px",
      borderRadius: "4px",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    },
  },
  arrow: {
    sx: {
      color: "#E5E2E1",
    },
  },
};

export const rangeStyles = {
  width: "70%",
  maxWidth: "245px",
  "& .MuiSlider-thumb": {
    backgroundColor: "#12B6EA",
    border: "2px solid #fff",
    boxShadow: "none",
  },
  "& .MuiSlider-rail": {
    backgroundColor: "var(--M-background96)",
    opacity: 1,
  },
  "& .MuiSlider-track": {
    backgroundColor: "#12B6EA",
  },
  "& .MuiSlider-mark": {
    backgroundColor: "#2A2C33",
    width: "10px",
    height: "10px",
    opacity: "10,",
    borderRadius: "50%",
  },
  "&:hover, &.Mui-focusVisible, &.Mui-active": {
    boxShadow: "none", // Remove hover and focus styles
  },
};

const PasteBtnStyle = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  borderRadius: "25px",
  textTransform: "unset",
  boxShadow: "none",
  border: "1px solid #12B6EA",
  padding: { md: "6px 8px" },
  color: "#12B6EA",
};

export const OptionBtnIconStyle = {
  width: "24px",
  color: "#12B6EA",
};

const aiAnalyzeStyle = {
  fontSize: "16px",
  "@media (max-width: 900px)": { fontSize: "14px" },
  "@media (max-width: 500px)": { fontSize: "12px" },
};
export const getSummarizeOutputValue = (param: number) => {
  return [
    SummarizeOutputValues.Low,
    SummarizeOutputValues.Medium,
    SummarizeOutputValues.High,
    SummarizeOutputValues.VeryHigh,
  ][param];
};
interface TextSuitesBodyProps {
  activeItem: ActiveItemInterface;
  setSelectedFromDropdownItem: (name: string) => void;
  setSelectedToDropdownItem: (name: string) => void;
  setInputValue: (name: string) => void;
  setOutputValue: (name: string) => void;
  setSliderValueAppSummarize?: (index: number) => void;
  selectedFromDropdownItem: string;
  selectedToDropdownItem: string;
  sliderValueForSummarize?: number;
  inputValue: string;
  outputValue: string;
  activeItemData: any;
  sourceWorkflow?: string;
}

export const TextSuitesBody = ({
  activeItem,
  setSelectedFromDropdownItem,
  setSelectedToDropdownItem,
  setSliderValueAppSummarize,
  selectedFromDropdownItem,
  selectedToDropdownItem,
  sliderValueForSummarize,
  activeItemData,
  inputValue,
  setInputValue,
  setOutputValue,
  outputValue,
  sourceWorkflow,
}: TextSuitesBodyProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const textSuitesInputRef = useRef<HTMLTextAreaElement>(null);
  const secInput = useRef<HTMLInputElement>(null);
  const [secInputValue, setSecInputValue] = useState("");
  const [secInputPrevValue, setSecInputPrevValue] = useState("");
  const [sliderValue, setSliderValue] = useState(1);
  const [isOutputCoped, setIsOutputCoped] = useState(false);
  const [isTitleCoped, setIsTitleCoped] = useState(false);
  const [titleCoped, setTitleCoped] = useState("");
  const mobileSize = useMediaQuery("(max-width: 900px)");
  const responsiveSize = useMediaQuery("(max-width: 700px)");
  const [generateTitlesOutput, setGenerateTitlesOutput] = useState<string[]>(
    []
  );
  const isUserAuthenticated = useAppSelector((s) => s.User.IsAuthenticated);
  const dispatch = useAppDispatch();
  const [extractOutput, setExtractOutput] = useState<string>("");
  const maxInputValueLength = 5000;
  const countInputLength = `${inputValue.length} / ${maxInputValueLength}`;
  const renderTowInputs =
    activeItem.title === TextSuiteApps.TitleGenerator &&
    (selectedFromDropdownItem === "YouTube video" ||
      selectedFromDropdownItem === "Slogan");
  const [inputLengthError, setInputLengthError] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const getUserCredits = useGetUserCredits();

  const { mutate: TextSuitesTranslateRequest, isPending: isTranslatePending } =
    useTextSuitesTranslate();
  const { mutate: TextSuitesHumanizerRequest, isPending: isHumanizerPending } =
    useTextSuitesHumanizer();
  const { mutate: TextSuitesSummarizeRequest, isPending: isSummarizePending } =
    useTextSuitesSummarize();
  const { mutate: GetSamplesTextSuite, data: SamplesTextSuite } =
    useGetTextSuitSamples();
  const {
    mutate: TextSuitesParaphraseRequest,
    isPending: isParaphrasePending,
  } = useTextSuitesParaphrase();
  const {
    mutate: TextSuitesTitleGeneratorRequest,
    isPending: isTitleGeneratorPending,
  } = useTextSuitesTitleGenerator();
  const {
    mutate: TextSuitesPromptEngineerRequest,
    isPending: isPromptEngineerPending,
  } = useTextSuitesPromptEngineer();
  const [textHistoryItem, setTextHistoryItem] = useState<string>("");
  const { data: TextHistoryItemData, isLoading: isHistoryItemLoading } =
    useGetTextSuiteHistoryById(textHistoryItem);
  const [textSuitesAppsData, setTextSuitesAppsData] = useState<{
    translator: TextSuiteAppInputText;
    humanizer: TextSuiteAppInputText;
    summarizer: TextSuiteAppInputText;
    paraphraser: TextSuiteAppInputText;
    titlegenerator: TextSuiteAppInputText;
    promptengineer: TextSuiteAppInputText;
    ImageTranscription: TextSuiteAppInputImage;
    ImageToPrompt: TextSuiteAppInputImage;
  }>(textSuiteAppInitStates);
  useEffect(() => {
    GetSamplesTextSuite();
  }, []);

  useEffect(() => {
    if (SamplesTextSuite) {
      SamplesTextSuite?.forEach((item: TextSuiteSample) => {
        if (item.type === TextSuiteType.TextExtraction) {
          setTextSuitesAppsData((prevState: any) => ({
            ...prevState,
            ImageTranscription: {
              ...prevState.ImageTranscription,
              example: {
                ...item,
              },
            },
          }));
        } else if (item.type === TextSuiteType.ImageToPrompt) {
          setTextSuitesAppsData((prevState: any) => ({
            ...prevState,
            ImageToPrompt: {
              ...prevState.ImageToPrompt,
              example: {
                ...item,
              },
            },
          }));
        }
      });
    }
  }, [SamplesTextSuite]);

  const getActiveAppObj = useMemo(() => {
    switch (activeItem.type) {
      case TextSuiteType.TextExtraction:
        return textSuitesAppsData.ImageTranscription;
      case TextSuiteType.ImageToPrompt:
        return textSuitesAppsData.ImageToPrompt;
      default:
        return null;
    }
  }, [activeItem, textSuitesAppsData]);

  useEffect(() => {
    if (
      activeItem.title === location.state?.activeAppType &&
      location.state?.activeAppId
    ) {
      setTextHistoryItem(location.state?.activeAppId);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (!isHistoryItemLoading) {
      setTextSuitesAppsData((prevState: typeof textSuitesAppsData) => {
        const newState = { ...prevState };
        switch (activeItem.title) {
          case TextSuiteApps.ImageToPrompt:
            newState.ImageToPrompt.isHistoryLoading = false;
            break;
          case TextSuiteApps.TextExtraction:
            newState.ImageTranscription.isHistoryLoading = false;
            break;
          default:
            break;
        }
        return newState;
      });
    }
  }, [location.state, activeItem]);

  // Handle set selectors of history item.
  useEffect(() => {
    if (TextHistoryItemData) {
      const appSetting = JSON.parse(TextHistoryItemData?.payload || "{}");
      setTextSuitesAppsData((prevState) => {
        const newState = { ...prevState };
        switch (activeItem.title) {
          case TextSuiteApps.Translator:
            newState.translator.input = TextHistoryItemData.input;
            newState.translator.output = TextHistoryItemData.output;
            newState.translator.isLoading = false;
            break;
          case TextSuiteApps.Humanizer:
            newState.humanizer.input = TextHistoryItemData.input;
            newState.humanizer.output = TextHistoryItemData.output;
            newState.humanizer.isLoading = false;
            break;
          case TextSuiteApps.Summarizer:
            newState.summarizer.input = TextHistoryItemData.input;
            newState.summarizer.output = TextHistoryItemData.output;
            newState.summarizer.isLoading = false;
            break;
          case TextSuiteApps.Paraphraser:
            newState.paraphraser.input = TextHistoryItemData.input;
            newState.paraphraser.output = TextHistoryItemData.output;
            newState.paraphraser.isLoading = false;
            break;
          case TextSuiteApps.TitleGenerator:
            newState.titlegenerator.input = TextHistoryItemData.input;
            newState.titlegenerator.output = TextHistoryItemData.output;
            newState.titlegenerator.isLoading = false;
            break;
          case TextSuiteApps.PromptGenerator:
            newState.promptengineer.input = TextHistoryItemData.input;
            newState.promptengineer.output = TextHistoryItemData.output;
            newState.promptengineer.isLoading = false;
            break;
          case TextSuiteApps.TextExtraction:
            newState.ImageTranscription.input = TextHistoryItemData.input;
            newState.ImageTranscription.output = TextHistoryItemData.output;
            newState.ImageTranscription.isHistoryLoading = false;
            break;
          case TextSuiteApps.ImageToPrompt:
            newState.ImageToPrompt.input = TextHistoryItemData.input;
            newState.ImageToPrompt.output = TextHistoryItemData.output;
            newState.ImageToPrompt.isHistoryLoading = false;
            break;
          default:
            break;
        }
        return newState;
      });
      switch (activeItem.title) {
        case TextSuiteApps.Translator:
          setSelectedFromDropdownItem(appSetting.SourceLanguage);
          setSelectedToDropdownItem(appSetting.TargetLanguage);
          break;
        case TextSuiteApps.Humanizer:
          setSelectedFromDropdownItem(appSetting.Style);
          break;
        case TextSuiteApps.Summarizer:
          let summarizerValue = getSummarizeOutputKeyIndexByValue(
            appSetting.TextLength
          );
          setSelectedFromDropdownItem(appSetting.Mode);
          setSliderValue(summarizerValue || 0);
          break;
        case TextSuiteApps.Paraphraser:
          setSelectedFromDropdownItem(appSetting.Mode);
          break;
        case TextSuiteApps.TitleGenerator:
          let generatedTitles = handleTitlesGeneration(
            TextHistoryItemData.output
          );
          setGenerateTitlesOutput(generatedTitles);
          setSelectedFromDropdownItem(appSetting.Purpose);
          setOutputValue(TextHistoryItemData.input);
          setSecInputValue(appSetting.Brand);
          break;
        default:
          break;
      }
      setInputValue(TextHistoryItemData.input);
    }
  }, [TextHistoryItemData]);

  useEffect(() => {
    if (isHistoryItemLoading) {
      setTextSuitesAppsData((prevState: typeof textSuitesAppsData) => {
        const newState = { ...prevState };
        switch (activeItem.title) {
          case TextSuiteApps.ImageToPrompt:
            newState.ImageToPrompt.isHistoryLoading = true;
            break;
          case TextSuiteApps.TextExtraction:
            newState.ImageTranscription.isHistoryLoading = true;
            break;
          default:
            break;
        }
        return newState;
      });
    }
  }, [isHistoryItemLoading]);

  useEffect(() => {
    if (
      isTitleGeneratorPending ||
      isTranslatePending ||
      isHumanizerPending ||
      isSummarizePending ||
      isParaphrasePending ||
      isPromptEngineerPending
    ) {
      setTextSuitesAppsData((prevState: typeof textSuitesAppsData) => {
        const newState = { ...prevState };
        switch (activeItem.title) {
          case TextSuiteApps.Translator:
            newState.translator.isLoading = true;
            break;
          case TextSuiteApps.Humanizer:
            newState.humanizer.isLoading = true;
            break;
          case TextSuiteApps.Summarizer:
            newState.summarizer.isLoading = true;
            break;
          case TextSuiteApps.Paraphraser:
            newState.paraphraser.isLoading = true;
            break;
          case TextSuiteApps.TitleGenerator:
            newState.titlegenerator.isLoading = true;
            break;
          case TextSuiteApps.PromptGenerator:
            newState.promptengineer.isLoading = true;
            break;
          case TextSuiteApps.TextExtraction:
            newState.ImageTranscription.isLoading = true;
            break;
          case TextSuiteApps.ImageToPrompt:
            newState.ImageToPrompt.isLoading = true;
            break;
          default:
            break;
        }
        return newState;
      });
    }
  }, [
    activeItem,
    isTranslatePending,
    isHumanizerPending,
    isSummarizePending,
    isParaphrasePending,
    isTitleGeneratorPending,
    isPromptEngineerPending,
  ]);

  const handleGenerateImageSuitesSignalR = useCallback(
    (
      modelName: string,
      modelVersion: string,
      message: string,
      tag: string,
      conversationId: string,
      messageId: string,
      messageType: number
    ) => {
      if (message === "**/**Finished**/**") {
        setIsFinished(true);
      } else {
        if (
          messageType === MessageType.TextSuiteExtraction &&
          message !== null
        ) {
          // setExtractOutput((prevOut) => prevOut + message);
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            ImageTranscription: {
              ...prevState.ImageTranscription,
              output: prevState.ImageTranscription.output + message,
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.TextSuiteImageToPrompt) {
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            ImageToPrompt: {
              ...prevState.ImageToPrompt,
              output: prevState.ImageToPrompt.output + message,
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.ImageSuiteError) {
          dispatch(
            setFileError({
              isError: true,
              errorMsg: message,
              reason: message,
            })
          );
        }
      }
    },
    []
  );

  const setupSignalRConnectionImage = useCallback(() => {
    const connection = SignalRService.getInstance().connection;
    connection.on("SendModelMessage", handleGenerateImageSuitesSignalR);
    return () => {
      connection.off("SendModelMessage", handleGenerateImageSuitesSignalR);
    };
  }, [handleGenerateImageSuitesSignalR]);

  useEffect(() => {
    const cleanup = setupSignalRConnectionImage();
    return () => {
      cleanup();
    };
  }, [setupSignalRConnectionImage]);
  //return loading status based on active item.
  const handleReturnLoadingStatus = useMemo(() => {
    switch (activeItem.title) {
      case TextSuiteApps.Translator:
        return textSuitesAppsData.translator.isLoading;
      case TextSuiteApps.Humanizer:
        return textSuitesAppsData.humanizer.isLoading;
      case TextSuiteApps.Summarizer:
        return textSuitesAppsData.summarizer.isLoading;
      case TextSuiteApps.Paraphraser:
        return textSuitesAppsData.paraphraser.isLoading;
      case TextSuiteApps.TitleGenerator:
        return textSuitesAppsData.titlegenerator.isLoading;
      case TextSuiteApps.PromptGenerator:
        return textSuitesAppsData.promptengineer.isLoading;
      default:
        return false;
    }
  }, [activeItem, textSuitesAppsData]);

  const [isActionButtonDisabled, setIsActionButtonDisabled] = useState(false);
  type TextSuitesAppsDataKeys = keyof typeof textSuitesAppsData;

  // Status of the action button activation.
  useEffect(() => {
    let prevInput =
      textSuitesAppsData[
        activeItem.title
          .toLowerCase()
          .replace(" ", "") as TextSuitesAppsDataKeys
      ]?.input;
    let buttonStatus =
      !inputValue.trim() ||
      handleReturnLoadingStatus ||
      (prevInput === inputValue &&
        !!outputValue &&
        secInputValue === secInputPrevValue);
    setIsActionButtonDisabled(buttonStatus);
    if (inputValue.length === 0 || inputValue.trim().length === 0) {
      setInputLengthError(false);
      handleClearInputValue();
    }
  }, [handleReturnLoadingStatus, inputValue, secInputValue]);

  useEffect(() => {
    if (inputValue && outputValue) {
      setIsActionButtonDisabled(false);
    }
    if (renderTowInputs && secInputValue.length === 0) {
      handleClearInputValue();
    }
  }, [selectedFromDropdownItem, selectedToDropdownItem, sliderValue]);

  const ActionBtnStyle = {
    display: "flex",
    alignItems: "center",
    borderRadius: "45.5px",
    textTransform: "unset",
    boxShadow: "none",
    bgcolor: "#12B6EA",
    padding: { md: "6px 24px" },
    color: "#fff",
    transition: "0.3s",
    opacity: isActionButtonDisabled ? "0.5" : "1",
    ":hover": {
      bgcolor: "#12B6EA",
    },
    "&.Mui-disabled": {
      backgroundColor: "#12B6EA",
      color: "#fff",
      cursor: "default",
      Opacity: "0.5",
    },
  };

  const inputStyles = {
    width: "100%",
    fontFamily: "Roboto, sans-serif",
    fontSize: "16px !important",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      height: "100%",
      padding: 0,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: renderTowInputs ? "1px solid red" : "none",
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      lineHeight: "1.5",
      color: "var(--Main_icons_color)",
      caretColor: "#12B6EA",
      overflowY: "auto !important", // Fix scrolling issue
      height: "100% !important",
      maxHeight: "100%", // Set a reasonable max height
    },
    "& .MuiInputBase-input::placeholder": {
      fontSize: "15px", // Set your desired font size here
      fontWight: 300,
    },
    "& textarea": {
      overflowY: "auto !important", // Ensure textarea can scroll
      maxHeight: "100%",
    },
    "& .MuiInputBase-input::-webkit-scrollbar": {
      width: "5px",
    },
    "& .MuiInputBase-input::-webkit-scrollbar-thumb": {
      backgroundColor: "var(--sub_background4)",
      borderRadius: "8px",
    },
  };

  // Get the Percentages of the ai detected.
  const extractPercentages = (output: string) => {
    // Use regex to extract all percentage values
    const matches = output.match(/\d+(\.\d+)?%/g);

    const values = matches
      ? matches.map((val) => parseFloat(val.replace("%", "")))
      : [];

    return {
      aiGenerated: values[0] ?? 0,
      aiRefined: values[1] ?? 0,
      humanWrittenAiRefined: values[2] ?? 0,
      humanWritten: values[3] ?? 0,
    };
  };

  const handleTitlesGeneration = (output: string) => {
    const lines = output
      .split("\n")
      .map((line) => line.replace(/^\d+\.\s*/, ""));
    const titles: string[] = [];

    lines.forEach((line) => {
      const cleanLine = line.trim();
      if (cleanLine) {
        titles.push(cleanLine); // Push each line as an item
      }
    });

    return titles;
  };

  useEffect(() => {
    if (!isFinished && activeItem.title === TextSuiteApps.TitleGenerator) {
      const output = textSuitesAppsData.titlegenerator.output || outputValue;
      const titles = handleTitlesGeneration(output);
      setGenerateTitlesOutput(titles);
    }
  }, [generateTitlesOutput, isFinished, activeItem]);

  useEffect(() => {
    if (isFinished) {
      setIsFinished(false);
      getUserCredits();
      setTextSuitesAppsData((prevState) => {
        const newState = { ...prevState };
        switch (activeItem.title) {
          case TextSuiteApps.Translator:
            newState.translator.isLoading = false;
            break;
          case TextSuiteApps.Humanizer:
            newState.humanizer.isLoading = false;
            break;
          case TextSuiteApps.Summarizer:
            newState.summarizer.isLoading = false;
            break;
          case TextSuiteApps.Paraphraser:
            newState.paraphraser.isLoading = false;
            break;
          case TextSuiteApps.TitleGenerator:
            newState.titlegenerator.isLoading = false;
            break;
          case TextSuiteApps.PromptGenerator:
            newState.promptengineer.isLoading = false;
            break;
          case TextSuiteApps.TextExtraction:
            newState.ImageTranscription.isLoading = false;
            break;
          case TextSuiteApps.ImageToPrompt:
            newState.ImageToPrompt.isLoading = false;
            break;
          default:
            break;
        }
        return newState;
      });
    }
  }, [isFinished]);

  // Set output value based on active item.
  useEffect(() => {
    switch (activeItem.title) {
      case TextSuiteApps.Translator:
        setOutputValue(textSuitesAppsData.translator.output);
        break;
      case TextSuiteApps.Humanizer:
        setOutputValue(textSuitesAppsData.humanizer.output);
        break;
      case TextSuiteApps.Summarizer:
        setOutputValue(textSuitesAppsData.summarizer.output);
        break;
      case TextSuiteApps.Paraphraser:
        setOutputValue(textSuitesAppsData.paraphraser.output);
        break;
      case TextSuiteApps.TitleGenerator:
        setOutputValue(textSuitesAppsData.titlegenerator.output);
        break;
      case TextSuiteApps.PromptGenerator:
        setOutputValue(textSuitesAppsData.promptengineer.output);
        break;
      default:
        break;
    }
  }, [textSuitesAppsData, activeItem]);

  // Set input value based on active item.
  useEffect(() => {
    switch (activeItem.title) {
      case TextSuiteApps.Translator:
        setInputValue(textSuitesAppsData.translator.input);
        break;
      case TextSuiteApps.Humanizer:
        setInputValue(textSuitesAppsData.humanizer.input);
        break;
      case TextSuiteApps.Summarizer:
        setInputValue(textSuitesAppsData.summarizer.input);
        break;
      case TextSuiteApps.Paraphraser:
        setInputValue(textSuitesAppsData.paraphraser.input);
        break;
      case TextSuiteApps.TitleGenerator:
        setInputValue(textSuitesAppsData.titlegenerator.input);
        break;
      case TextSuiteApps.PromptGenerator:
        setInputValue(textSuitesAppsData.promptengineer.input);
        break;
      default:
        break;
    }
  }, [activeItem]);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number);
    if (
      sourceWorkflow === CreateWorkflow &&
      activeItem.title === TextSuiteApps.Summarizer &&
      setSliderValueAppSummarize
    )
      setSliderValueAppSummarize(newValue as number);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };

  const handleSecInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSecInputValue(event.target.value);
  };

  const handlePasteText = () => {
    if (
      sourceWorkflow !== CreateWorkflow &&
      sourceWorkflow !== CreateWorkflowPreview
    ) {
      navigator.clipboard
        .readText()
        .then((text) => {
          if (textSuitesInputRef && textSuitesInputRef.current) {
            textSuitesInputRef.current.value = text;
            setInputValue(text);
            setTextSuitesAppsData((prevState) => {
              const newState = { ...prevState };
              switch (activeItem.title) {
                case TextSuiteApps.Translator:
                  newState.translator.input = text;
                  break;
                case TextSuiteApps.Humanizer:
                  newState.humanizer.input = text;
                  break;
                case TextSuiteApps.Summarizer:
                  newState.summarizer.input = text;
                  break;
                case TextSuiteApps.Paraphraser:
                  newState.paraphraser.input = text;
                  break;
                case TextSuiteApps.TitleGenerator:
                  newState.titlegenerator.input = text;
                  break;
                case TextSuiteApps.PromptGenerator:
                  newState.promptengineer.input = text;
                  break;
                default:
                  break;
              }
              return newState;
            });
          }
        })
        .catch((err) => {
          console.error("Failed to read clipboard contents: ", err);
        });
    }
  };

  const handleCopyOutput = () => {
    const outputText = (() => {
      switch (activeItem.title) {
        case TextSuiteApps.Translator:
          return textSuitesAppsData.translator.output;
        case TextSuiteApps.Humanizer:
          return textSuitesAppsData.humanizer.output;
        case TextSuiteApps.Summarizer:
          return textSuitesAppsData.summarizer.output;
        case TextSuiteApps.Paraphraser:
          return textSuitesAppsData.paraphraser.output;
        case TextSuiteApps.TitleGenerator:
          return textSuitesAppsData.titlegenerator.output;
        case TextSuiteApps.PromptGenerator:
          return textSuitesAppsData.promptengineer.output;
        default:
          return "";
      }
    })();
    if (outputText) {
      navigator.clipboard.writeText(outputText);
      setIsOutputCoped(true);
      setTimeout(() => {
        setIsOutputCoped(false);
      }, 1000);
    }
  };

  const handleCopyTitleGenerated = (title: string) => {
    if (title) {
      navigator.clipboard.writeText(title);
      setIsTitleCoped(true);
      setTitleCoped(title);
      setTimeout(() => {
        setIsTitleCoped(false);
      }, 1000);
    }
  };

  const handleOutputValue = () => {
    setTextSuitesAppsData((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.title) {
        case TextSuiteApps.Translator:
          newState.translator.input = "";
          newState.translator.output = "";
          break;
        case TextSuiteApps.Humanizer:
          newState.humanizer.input = "";
          newState.humanizer.output = "";
          break;
        case TextSuiteApps.Summarizer:
          newState.summarizer.input = "";
          newState.summarizer.output = "";
          break;
        case TextSuiteApps.Paraphraser:
          newState.paraphraser.input = "";
          newState.paraphraser.output = "";
          break;
        case TextSuiteApps.TitleGenerator:
          newState.titlegenerator.input = "";
          newState.titlegenerator.output = "";
          break;
        case TextSuiteApps.PromptGenerator:
          newState.promptengineer.input = "";
          newState.promptengineer.output = "";
          break;
        default:
          break;
      }
      return newState;
    });
  };

  const handleClearInputValue = () => {
    if (textSuitesInputRef.current) {
      setInputValue("");
      textSuitesInputRef.current.value = "";
      handleOutputValue();
      setGenerateTitlesOutput([]);
      if (secInput.current) {
        secInput.current.value = "";
        setSecInputValue("");
      }
    }
  };

  const countWords = (text: string): number => {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  const wordCount = countWords(inputValue);

  const handleSwitchSelectedLanguages = () => {
    if (
      selectedFromDropdownItem &&
      selectedToDropdownItem &&
      selectedFromDropdownItem !== activeItemData.defaultSelectorOption
    ) {
      setSelectedFromDropdownItem(selectedToDropdownItem);
      setSelectedToDropdownItem(selectedFromDropdownItem);
    }
  };

  // =======================================================================
  // Signal-R functions.

  const handleTranslateAndImproveMessage = useCallback(
    (
      modelName: string,
      modelVersion: string,
      message: string,
      tag: string,
      conversationId: string,
      messageId: string,
      messageType: number
    ) => {
      if (message === "**/**Finished**/**") {
        setIsFinished(true);
      } else {
        if (messageType === MessageType.TextSuiteTranslate) {
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            translator: {
              ...prevState.translator,
              output: prevState.translator.output + message,
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.TextSuiteHumanize) {
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            humanizer: {
              ...prevState.humanizer,
              output: prevState.humanizer.output + message,
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.TextSuiteSummarize) {
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            summarizer: {
              ...prevState.summarizer,
              output: prevState.summarizer.output + message,
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.TextSuiteParaPhrase) {
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            paraphraser: {
              ...prevState.paraphraser,
              output: prevState.paraphraser.output + message,
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.TextSuiteTitleGenerator) {
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            titlegenerator: {
              ...prevState.titlegenerator,
              output: prevState.titlegenerator.output + message,
              isLoading: true,
            },
          }));
        } else if (messageType === MessageType.TextSuitePromptEngineer) {
          setTextSuitesAppsData((prevState) => ({
            ...prevState,
            promptengineer: {
              ...prevState.promptengineer,
              output: prevState.promptengineer.output + message,
              isLoading: true,
            },
          }));
        }
      }
    },
    []
  );

  const setupSignalRConnection = useCallback(() => {
    const connection = SignalRService.getInstance().connection;
    connection.on("SendModelMessage", handleTranslateAndImproveMessage);
    return () => {
      connection.off("SendModelMessage", handleTranslateAndImproveMessage);
    };
  }, [handleTranslateAndImproveMessage]);

  useEffect(() => {
    const cleanup = setupSignalRConnection();
    return () => {
      cleanup();
    };
  }, [setupSignalRConnection]);

  // =======================================================================
  // Endpoints apps of Text Suites.

  const handleTextSuitesTranslator = async () => {
    const connectionId = await SignalRService.getInstance().getConnectionId();

    const translateRequestData = {
      prompt: inputValue,
      sourceLanguage: selectedFromDropdownItem,
      targetLanguage: selectedToDropdownItem,
      connectionId: connectionId,
    };
    TextSuitesTranslateRequest(translateRequestData);
    setTextSuitesAppsData((prevState) => {
      return {
        ...prevState,
        translator: {
          ...prevState.translator,
          input: inputValue,
        },
      };
    });
  };

  const handleTextSuitesHumanizer = async () => {
    if (selectedFromDropdownItem) {
      const connectionId = await SignalRService.getInstance().getConnectionId();

      const humanizerRequestData = {
        prompt: inputValue,
        style: selectedFromDropdownItem,
        connectionId: connectionId,
      };
      TextSuitesHumanizerRequest(humanizerRequestData);
      setTextSuitesAppsData((prevState) => {
        return {
          ...prevState,
          humanizer: {
            ...prevState.humanizer,
            input: inputValue,
          },
        };
      });
    }
  };

  const handleTextSuitesSummarize = async () => {
    if (selectedFromDropdownItem) {
      const connectionId = await SignalRService.getInstance().getConnectionId();
      const textLength = getSummarizeOutputValue(sliderValue);
      const summarizeRequestData = {
        prompt: inputValue,
        mode: selectedFromDropdownItem,
        connectionId: connectionId,
        textLength: textLength,
      };
      TextSuitesSummarizeRequest(summarizeRequestData);
      setTextSuitesAppsData((prevState) => {
        return {
          ...prevState,
          summarizer: {
            ...prevState.summarizer,
            input: inputValue,
          },
        };
      });
    }
  };

  const handleTextSuitesParaphrase = async () => {
    if (selectedFromDropdownItem) {
      const connectionId = await SignalRService.getInstance().getConnectionId();

      const paraphraseRequestData = {
        prompt: inputValue,
        mode: selectedFromDropdownItem,
        connectionId: connectionId,
      };
      TextSuitesParaphraseRequest(paraphraseRequestData);
      setTextSuitesAppsData((prevState) => {
        return {
          ...prevState,
          paraphraser: {
            ...prevState.paraphraser,
            input: inputValue,
          },
        };
      });
    }
  };

  const handleTextSuitesTitleGenerator = async () => {
    if (selectedFromDropdownItem) {
      if (renderTowInputs && inputValue.length <= 50) {
        setInputLengthError(true);
      } else {
        setInputLengthError(false);
        setGenerateTitlesOutput([]);
        const connectionId =
          await SignalRService.getInstance().getConnectionId();
        let TitleGeneratorRequestData;
        if (
          selectedFromDropdownItem === "YouTube video" ||
          selectedFromDropdownItem === "Slogan"
        ) {
          TitleGeneratorRequestData = {
            prompt: inputValue,
            purpose: selectedFromDropdownItem,
            connectionId: connectionId,
            brand: secInputValue,
          };
          setSecInputPrevValue(secInputValue);
        } else {
          TitleGeneratorRequestData = {
            prompt: inputValue,
            purpose: selectedFromDropdownItem,
            connectionId: connectionId,
          };
        }
        TextSuitesTitleGeneratorRequest(TitleGeneratorRequestData);
        setTextSuitesAppsData((prevState) => {
          return {
            ...prevState,
            titlegenerator: {
              ...prevState.titlegenerator,
              input: inputValue,
            },
          };
        });
      }
    }
  };

  const handleTextSuitesPromptEngineer = async () => {
    const connectionId = await SignalRService.getInstance().getConnectionId();

    const paraphraseRequestData = {
      prompt: inputValue,
      connectionId: connectionId,
    };
    TextSuitesPromptEngineerRequest(paraphraseRequestData);
    setTextSuitesAppsData((prevState) => {
      return {
        ...prevState,
        promptengineer: {
          ...prevState.promptengineer,
          input: inputValue,
        },
      };
    });
  };

  const handleButtonAction = (isReGenerate?: boolean) => {
    if (!isUserAuthenticated)
      dispatch(
        setShowSignPopup({
          show: true,
          type: "Explore Apps",
          message: "To complete the process you need to Sign in.",
          btnTitle: "Sign In for Free",
          btnAction: "NavigateToLogin",
        })
      );
    else {
      if (!isActionButtonDisabled || isReGenerate) {
        handleOutputValue();
        if (activeItem.title === TextSuiteApps.Translator) {
          handleTextSuitesTranslator();
        } else if (activeItem.title === TextSuiteApps.Humanizer) {
          handleTextSuitesHumanizer();
        } else if (activeItem.title === TextSuiteApps.Summarizer) {
          handleTextSuitesSummarize();
        } else if (activeItem.title === TextSuiteApps.Paraphraser) {
          handleTextSuitesParaphrase();
        } else if (activeItem.title === TextSuiteApps.TitleGenerator) {
          handleTextSuitesTitleGenerator();
        } else if (activeItem.title === TextSuiteApps.PromptGenerator) {
          handleTextSuitesPromptEngineer();
        }
      }
    }
  };

  const handleReGenerateResponse = () => {
    handleOutputValue();
    handleButtonAction(true);
  };

  const handleClearOutput = () => {
    setTextSuitesAppsData((prevState: typeof textSuitesAppsData) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case TextSuiteType.ImageToPrompt:
          newState.ImageToPrompt.output = "";
          break;
        case TextSuiteType.TextExtraction:
          newState.ImageTranscription.output = "";
          break;
        default:
          break;
      }
      return newState;
    });
  };

  const handleInputImageChange = (image: File | null) => {
    setTextSuitesAppsData((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case TextSuiteType.TextExtraction:
          newState.ImageTranscription.input = image;
          break;
        case TextSuiteType.ImageToPrompt:
          newState.ImageToPrompt.input = image;
          break;
        default:
          break;
      }
      return newState;
    });
  };

  const handleClearInput = () => {
    handleInputImageChange(null);
    setTextSuitesAppsData((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case TextSuiteType.TextExtraction:
          newState.ImageTranscription.input = "";
          break;
        case TextSuiteType.ImageToPrompt:
          newState.ImageToPrompt.input = "";
          break;
        default:
          break;
      }
      return newState;
    });
  };

  const getErrorMessage = (error: any) => {
    const responseData = error?.response?.data;
    if (responseData) {
      dispatch(setMessageErrorAppSuite(responseData));
    }
  };

  const handleSetIsLoading = (isLoading: boolean) => {
    setTextSuitesAppsData((prevState) => {
      const newState = { ...prevState };
      switch (activeItem.type) {
        case TextSuiteType.TextExtraction:
          newState.ImageTranscription.isLoading = isLoading;
          break;
        case TextSuiteType.ImageToPrompt:
          newState.ImageToPrompt.isLoading = isLoading;
          break;
        default:
          break;
      }
      return newState;
    });
  };
  // Map each title to a specific message
  const TextSuiteAppTitles = {
    [TextSuiteApps.Translator]: "Understand or write in any language fast",
    [TextSuiteApps.Humanizer]: "Turn AI text into natural language",
    [TextSuiteApps.Summarizer]: "Transform Long Texts to Key Points in seconds",
    [TextSuiteApps.Paraphraser]: "Say the same thing in a better way",
    [TextSuiteApps.TitleGenerator]: "Create engaging headlines instantly",
    [TextSuiteApps.PromptGenerator]:
      " Generate smart, optimized prompts and get the best results",
    [TextSuiteApps.AiDetector]: "Check if a text is AI-generated",
    [TextSuiteApps.TextExtraction]: "Extract text from documents.",
    [TextSuiteApps.ImageToPrompt]: "Generate prompts from images.",
  };
  // Usage example:
  function getTitleForActiveItem(activeItem: string) {
    return TextSuiteAppTitles[activeItem] || "";
  }
  return (
    <>
      {getActiveAppObj?.type === TextSuiteType.ImageToPrompt ? (
        <Box className="generation-view">
          <ImageToPromptApp
            generatedImg={getActiveAppObj.output}
            input={getActiveAppObj.input}
            handleClearInput={handleClearInput}
            getErrorMessage={getErrorMessage}
            handleSetIsLoading={handleSetIsLoading}
            handleInputImageChange={handleInputImageChange}
            isGenerateImgLoading={getActiveAppObj.isLoading}
            isGenerateButtonDisabled={getActiveAppObj.isLoading}
            clearExtractedOutput={handleClearOutput}
            isHistoryLoading={getActiveAppObj.isHistoryLoading}
            example={{
              ...getActiveAppObj.example,
            }}
          />
        </Box>
      ) : (
        getActiveAppObj?.type === TextSuiteType.TextExtraction && (
          <Box className="generation-view">
            <TextExtractionApp
              generatedImg={getActiveAppObj.output}
              input={getActiveAppObj.input}
              handleClearInput={handleClearInput}
              getErrorMessage={getErrorMessage}
              handleSetIsLoading={handleSetIsLoading}
              handleInputImageChange={handleInputImageChange}
              isGenerateImgLoading={getActiveAppObj.isLoading}
              isGenerateButtonDisabled={getActiveAppObj.isLoading}
              isHistoryLoading={getActiveAppObj.isHistoryLoading}
              clearExtractedOutput={handleClearOutput}
              example={{
                ...getActiveAppObj.example,
              }}
            />
          </Box>
        )
      )}
      {getActiveAppObj?.type !== TextSuiteType.TextExtraction &&
        getActiveAppObj?.type !== TextSuiteType.ImageToPrompt && (
          <Box className="text-suite-container">
            <Box
              className="text-suites-body"
              sx={{
                boxShadow:
                  sourceWorkflow === CreateWorkflow ||
                  sourceWorkflow === CreateWorkflowPreview
                    ? "none"
                    : "0px 0px 8px 0px var(--M-background94)",
                border:
                  sourceWorkflow === CreateWorkflow ||
                  sourceWorkflow === CreateWorkflowPreview
                    ? "1px solid var(--M-background94)"
                    : "none",
              }}
            >
              {activeItemData?.selectorOptions && (
                <>
                  <Box className="image-suites-input-header">
                    <Typography className="image-suites-input-header-title long-header-app">
                      {getTitleForActiveItem(activeItem.title)}
                    </Typography>
                  </Box>
                  <Box
                    className={`text-suites-body-head ${activeItem.title.toLocaleLowerCase()}`}
                  >
                    <DropdownSelector
                      selectorType={activeItem.title}
                      selectorFn={setSelectedFromDropdownItem}
                      selectedItem={selectedFromDropdownItem}
                      disabledItem={selectedToDropdownItem}
                      activeItemData={activeItemData}
                      sourceWorkflow={sourceWorkflow}
                    />
                    {activeItem.title === TextSuiteApps.Summarizer &&
                      selectedFromDropdownItem === "Paragraph" && (
                        <Box className="paragraph-range">
                          <Typography className="paragraph-range-text">
                            Short
                          </Typography>
                          <Slider
                            aria-label="Temperature"
                            value={
                              sourceWorkflow === TextSuites
                                ? sliderValue
                                : sliderValueForSummarize
                            }
                            valueLabelDisplay="off"
                            disabled={sourceWorkflow === CreateWorkflowPreview}
                            step={1}
                            marks
                            min={0}
                            max={3}
                            sx={rangeStyles}
                            onChange={handleSliderChange}
                          />
                          <Typography className="paragraph-range-text">
                            Long
                          </Typography>
                        </Box>
                      )}
                    {activeItem.title === TextSuiteApps.Translator && (
                      <Box className="translate-options">
                        <Box
                          className="switch-icon"
                          onClick={handleSwitchSelectedLanguages}
                        >
                          <IconButton color="inherit">
                            <Tooltip
                              disableHoverListener={responsiveSize}
                              disableFocusListener={responsiveSize}
                              disableTouchListener={responsiveSize}
                              title="Swap language"
                              arrow
                              componentsProps={tooltipStyles}
                            >
                              <SwapHorizIcon />
                            </Tooltip>
                          </IconButton>
                        </Box>
                        <Box sx={{ mx: 5 }}>
                          <DropdownSelector
                            hideDefault={true}
                            selectorFn={setSelectedToDropdownItem}
                            selectedItem={selectedToDropdownItem}
                            disabledItem={selectedFromDropdownItem}
                            selectorType={activeItem.title}
                            activeItemData={activeItemData}
                            switchSelectors={handleSwitchSelectedLanguages}
                            sourceWorkflow={sourceWorkflow}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              )}
              {activeItem.title === TextSuiteApps.PromptGenerator && (
                <Box className="image-suites-input-header">
                  <Typography className="image-suites-input-header-title long-header-app">
                    {getTitleForActiveItem(activeItem.title)}
                  </Typography>
                </Box>
              )}
              <Box
                className={`text-suites-body-boxes ${
                  activeItem.title === TextSuiteApps.PromptGenerator
                    ? "ai-section"
                    : ""
                }`}
              >
                <Box
                  className={`text-suites-input ${activeItemData?.appTitle
                    ?.toLowerCase()
                    .replace(" ", "")}`}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <TextField
                    value={inputValue}
                    inputRef={textSuitesInputRef}
                    onChange={handleInputChange}
                    label={
                      renderTowInputs && selectedFromDropdownItem === "Slogan"
                        ? "Brief Description"
                        : selectedFromDropdownItem === "YouTube video"
                        ? "Description"
                        : undefined
                    }
                    placeholder={
                      selectedFromDropdownItem === "Slogan"
                        ? activeItemData.sloganDesPlaceholder
                        : selectedFromDropdownItem === "YouTube video"
                        ? activeItemData.youtubeDesPlaceholder
                        : activeItemData?.inputDesc
                    }
                    multiline
                    minRows={2}
                    focused={renderTowInputs}
                    sx={{
                      ...inputStyles,
                      height: renderTowInputs ? "40%" : "89%",
                      overflow: !renderTowInputs ? "auto" : undefined,
                      mt: "1px",
                      "@media (max-width: 700px)": {
                        marginBottom: "1rem",
                      },
                    }}
                    maxRows={renderTowInputs ? 2 : undefined}
                    inputProps={{
                      maxLength: 5000, // Limit input to 5000 characters
                      readOnly: sourceWorkflow !== "TextSuites",
                    }}
                    error={inputLengthError}
                    helperText={
                      inputLengthError &&
                      "Brief Description field is too short (< 50 characters)."
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: inputLengthError ? "red" : "green",
                        fontWeight: "bold",
                      },
                    }}
                  />
                  {renderTowInputs && (
                    <TextField
                      onChange={handleSecInputChange}
                      value={secInputValue}
                      placeholder={
                        selectedFromDropdownItem === "Slogan"
                          ? activeItemData.sloganCompanyName
                          : activeItemData.youtubeKeysPlaceholder
                      }
                      ref={secInput}
                      label={
                        selectedFromDropdownItem === "Slogan"
                          ? "Brand or Company Name"
                          : selectedFromDropdownItem === "YouTube video"
                          ? "Inspirational Keywords"
                          : undefined
                      }
                      maxRows={2}
                      focused
                      sx={{ ...inputStyles }}
                    />
                  )}
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row-reverse",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        className="action-text-button"
                        onClick={() => handleButtonAction(false)}
                        sx={{
                          cursor: isActionButtonDisabled
                            ? "default"
                            : "pointer",
                          "@media (max-width: 500px)": {
                            "& .css-1lrmp2t-MuiTypography-root": {
                              fontSize: "14px",
                            },
                          },
                        }}
                      >
                        <IconButton
                          sx={ActionBtnStyle}
                          disabled={isActionButtonDisabled}
                        >
                          {handleReturnLoadingStatus ? (
                            <CircularProgress
                              size={20}
                              color="inherit"
                              sx={{ marginRight: "8px" }}
                            />
                          ) : null}
                          <Typography
                            sx={{
                              fontSize: "16px",
                              fontWeight: 500,
                              lineHeight: "24px",
                            }}
                          >
                            {handleReturnLoadingStatus
                              ? activeItemData.loadingMsg
                              : activeItemData?.btnActionTitle}
                          </Typography>
                        </IconButton>
                      </Box>
                      {inputValue.length > 0 && !handleReturnLoadingStatus ? (
                        <Box className="clear-input">
                          <Box
                            className="clear-input-icon"
                            onClick={handleClearInputValue}
                          >
                            <Tooltip
                                disableHoverListener={responsiveSize}
                                disableFocusListener={responsiveSize}
                                disableTouchListener={responsiveSize}
                                title="Clear text"
                                arrow
                                placement="top"
                                componentsProps={tooltipStyles}
                              >
                                  <ButtonBase>
                                    <ClearIcon className="clear-icon" />
                                  </ButtonBase>
                              </Tooltip>
                          </Box>
                          <Typography className="clear-input-count">
                            {activeItem.title === TextSuiteApps.Summarizer ||
                            activeItem.title === TextSuiteApps.Paraphraser
                              ? `${wordCount} words`
                              : countInputLength}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Box>

                <Box
                  className={`text-suites-output ${
                    outputValue.length === 0 && "output-empty"
                  }`}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {activeItem.title === TextSuiteApps.TitleGenerator ? (
                    <Box
                      className={`text-suites-output-msg ${
                        generateTitlesOutput.length > 0 && "title-generation"
                      }`}
                    >
                      {generateTitlesOutput.length > 0 ? (
                        generateTitlesOutput.map((title, i) => (
                          <Box key={i} className="title-box-container">
                            <Box className="title-box">
                              <Box className="title-box-content">
                                <Typography className="title-number">{`${
                                  i + 1
                                }.`}</Typography>
                                <Typography className="title-name">
                                  {title}
                                </Typography>
                              </Box>
                              <Box
                                onClick={() => handleCopyTitleGenerated(title)}
                              >
                                <IconButton color="inherit" size="small">
                                  <Tooltip
                                    disableHoverListener={responsiveSize}
                                    disableFocusListener={responsiveSize}
                                    disableTouchListener={responsiveSize}
                                    title={
                                      isTitleCoped ? "Copied" : "Copy title"
                                    }
                                    arrow
                                    placement="top"
                                    componentsProps={tooltipStyles}
                                  >
                                    {isTitleCoped && title === titleCoped ? (
                                      <DoneIcon
                                        sx={{
                                          ...OptionBtnIconStyle,
                                          color: "var(--Main_icons_color)",
                                        }}
                                      />
                                    ) : (
                                      <ContentCopyIcon
                                        sx={{
                                          ...OptionBtnIconStyle,
                                          color: "var(--Main_icons_color)",
                                        }}
                                      />
                                    )}
                                  </Tooltip>
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box>{activeItemData?.outputDesc}</Box>
                      )}
                    </Box>
                  ) : (
                    <Box className="text-suites-output-msg">
                      {outputValue.length === 0 ? (
                        activeItemData?.outputDesc
                      ) : (
                        <Box mt={-1}>
                          <ChatResponseFormat response={outputValue} />
                        </Box>
                      )}
                    </Box>
                  )}
                  {outputValue.length !== 0 &&
                  activeItem.title === TextSuiteApps.TitleGenerator ? (
                    <Box className="line-separator"></Box>
                  ) : null}
                  {outputValue.length > 0 && (
                    <Box
                      className="output-options-btns"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Box
                        className="re-generate-option"
                        onClick={handleReGenerateResponse}
                      >
                        <IconButton color="inherit" size="medium">
                          <Tooltip
                            disableHoverListener={responsiveSize}
                            disableFocusListener={responsiveSize}
                            disableTouchListener={responsiveSize}
                            title="Re-generate"
                            arrow
                            placement="top"
                            componentsProps={tooltipStyles}
                          >
                            <RefreshIcon sx={OptionBtnIconStyle} />
                          </Tooltip>
                        </IconButton>
                      </Box>
                      <Box
                        className="copy-all-option"
                        onClick={handleCopyOutput}
                      >
                        <IconButton color="inherit" size="small">
                          <Tooltip
                            disableHoverListener={responsiveSize}
                            disableFocusListener={responsiveSize}
                            disableTouchListener={responsiveSize}
                            title={isOutputCoped ? "Copied" : "Copy all"}
                            arrow
                            placement="top"
                            componentsProps={tooltipStyles}
                          >
                            {isOutputCoped ? (
                              <DoneIcon sx={OptionBtnIconStyle} />
                            ) : (
                              <ContentCopyIcon sx={OptionBtnIconStyle} />
                            )}
                          </Tooltip>
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
    </>
  );
};
